<section class="rounded-2xl border border-border bg-card shadow-sm">
  <!--
    EB-3883: offset aligned to the now-sticky invoice-page hero (sticky top-[50px],
    ~114px tall → pins through ~164px). Header sticks just below it so it never
    slides under the hero. z-10 keeps it beneath the hero's z-20.
  -->
  <header
    class="sticky top-[164px] z-10 flex cursor-pointer items-center gap-2.5 rounded-2xl bg-card p-5"
    [class.shadow-sm]="!collapsed()"
    (click)="toggle()"
  >
    <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
      <hlm-icon name="lucideWallet" size="1rem" />
    </span>
    <h2 class="font-display text-lg font-semibold text-foreground">Billing Details</h2>
    @if (!loading()) {
      <hlm-badge variant="secondary" class="ml-1 text-sm">{{ filteredRows().length }}</hlm-badge>
    }
    <hlm-icon
      [name]="collapsed() ? 'lucideChevronDown' : 'lucideChevronUp'"
      size="1rem"
      class="ml-auto text-muted-foreground"
    />
  </header>

  @if (!collapsed()) {
    <div class="flex flex-col gap-4 p-5 pt-4" (click)="$event.stopPropagation()">
      <!-- FILTER BAR -->
      <div class="rounded-xl border border-border bg-muted/30 p-4">
        <div class="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
          <hlm-form-field>
            <label hlmLabel>Category</label>
            <select hlmNativeSelect [formControl]="filterForm.controls.categoryKey">
              @for (cat of categories; track cat.key) {
                <option [value]="cat.key">{{ cat.label }}</option>
              }
            </select>
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>From</label>
            <input hlmInput type="date" [formControl]="filterForm.controls.fromDate" />
          </hlm-form-field>

          <hlm-form-field>
            <label hlmLabel>To</label>
            <input hlmInput type="date" [formControl]="filterForm.controls.toDate" />
            @if (dateRangeInvalid()) {
              <p hlmFieldError>From date must be on or before To date.</p>
            }
          </hlm-form-field>

          <button
            hlmBtn
            variant="default"
            (click)="reload()"
            [disabled]="loading() || dateRangeInvalid()"
          >
            @if (loading()) {
              <hlm-spinner size="16" />
              Loading…
            } @else {
              <hlm-icon name="lucideFilter" size="0.9rem" />
              Apply
            }
          </button>
        </div>
      </div>

      <!-- SEARCH BAR -->
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <hlm-icon
            name="lucideSearch"
            size="1rem"
            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            hlmInput
            type="search"
            class="w-full pl-10"
            placeholder="Search by feature, description, user account, invoice, or document number…"
            [value]="searchQuery()"
            (input)="onSearchInput($event)"
          />
        </div>
        <!-- EB-4502: feature column shows codes instead of English + KV text. -->
        <hlm-switch
          class="shrink-0"
          data-testid="billing-show-codes"
          label="Show codes"
          [checked]="showFeatureCodes()"
          (checkedChange)="setShowFeatureCodes($event)"
        />
        <button hlmBtn variant="ghost" size="icon" hlmTooltip="Refresh" (click)="reload()">
          <hlm-icon name="lucideRefreshCw" size="0.9rem" />
        </button>
      </div>

      <!-- TABLE -->
      @if (loading()) {
        <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <hlm-spinner size="20" />
          Loading billing events…
        </div>
      } @else if (filteredRows().length === 0) {
        <div
          class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground"
        >
          No billing events in this range
        </div>
      } @else {
        <div class="overflow-hidden rounded-xl border border-border">
          <div class="max-h-[560px] overflow-auto">
            <table class="w-full text-sm">
              <thead class="sticky top-0 z-10 bg-muted/80 backdrop-blur">
                <tr class="cmt-eyebrow whitespace-nowrap">
                  <th class="px-3 py-2 text-left">Date</th>
                  <th class="px-3 py-2 text-left">Time</th>
                  <th class="w-px px-3 py-2 text-right"><abbr title="User Account" class="no-underline">UA</abbr></th>
                  <th class="px-3 py-2 text-left">Feature</th>
                  @if (categoryKey() === 'ALL') {
                    <th class="px-3 py-2 text-left">Category</th>
                  }
                  <th class="w-px px-3 py-2 text-right"><abbr title="Quantity" class="no-underline">Qty</abbr></th>
                  <th class="px-3 py-2 text-right">Price</th>
                  <th class="px-3 py-2 text-left">Description</th>
                  <th class="px-3 py-2 text-left">Invoice ID</th>
                  <th class="px-3 py-2 text-left">Document</th>
                </tr>
              </thead>
              <tbody>
                @for (row of visibleRows(); track row.id) {
                  <tr
                    class="border-t border-border text-foreground transition-colors"
                    [ngClass]="{
                      'bg-destructive/10 hover:bg-destructive/15': isInvoiced(row),
                      'hover:bg-muted/60': !isInvoiced(row)
                    }"
                  >
                    <td class="px-3 py-2 font-mono-feat">
                      {{ row.billingTime | date:'dd.MM.yyyy' }}
                    </td>
                    <td class="px-3 py-2 font-mono-feat text-muted-foreground">
                      {{ row.billingTime | date:'HH:mm' }}
                    </td>
                    <td class="w-px px-3 py-2 text-right font-mono-feat">{{ row.userAccountId }}</td>
                    <td class="min-w-[240px] px-3 py-2" data-testid="feature-cell">
                      @let texts = featureTexts(row);
                      <!-- max-width on the div: a max-width on a <td> is undefined in table layout. -->
                      <div class="flex min-w-0 max-w-[360px] items-start gap-2">
                        @if (showFeatureCodes()) {
                          <span class="min-w-0 break-words font-mono-feat text-foreground">{{ texts.code }}</span>
                        } @else {
                          <!-- Two lines (EB-4502): English, then the KV wording staff know. -->
                          <span class="min-w-0">
                            <span class="block text-foreground">{{ texts.english }}</span>
                            @if (featureSecondLine(texts); as second) {
                              <span class="block text-muted-foreground">{{ second }}</span>
                            }
                          </span>
                        }
                        <cmt-feature-help class="mt-0.5 flex-none" [texts]="texts" />
                      </div>
                    </td>
                    @if (categoryKey() === 'ALL') {
                      <td class="whitespace-nowrap px-3 py-2 text-muted-foreground">
                        {{ categoryLabelOf(row) ?? '—' }}
                      </td>
                    }
                    <td class="w-px px-3 py-2 text-right font-mono-feat">
                      {{ row.amount | number:'1.0-4' }}
                    </td>
                    <td class="px-3 py-2 text-right font-mono-feat">
                      {{ row.unitPrice | number:'1.2-2' }}
                    </td>
                    <td class="max-w-[240px] px-3 py-2 text-muted-foreground">
                      <span
                        class="block truncate transition-colors hover:text-foreground"
                        [title]="row.info ?? ''"
                      >
                        {{ row.info || '—' }}
                      </span>
                    </td>
                    <td class="px-3 py-2 font-mono-feat">
                      @if (row.invoiceId) {
                        @let officialId = officialInvoiceNumber(row.invoiceId);
                        <span class="inline-flex items-center gap-1.5">
                          <span class="text-foreground">{{ row.invoiceId }}</span>
                          @if (officialId !== null) {
                            <button
                              type="button"
                              [hlmTooltip]="'Go to invoice ' + officialId"
                              (click)="jumpToInvoice.emit(row.invoiceId!); $event.stopPropagation()"
                              class="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                              aria-label="Go to invoice"
                            >
                              <hlm-icon name="lucideArrowUpRight" size="0.8rem" />
                            </button>
                          }
                        </span>
                      } @else {
                        <span class="text-muted-foreground/60">—</span>
                      }
                    </td>
                    @let docSpan = documentCellSpan(row);
                    @if (docSpan > 0) {
                    <!-- One cell + one delete button per call (EB-4384): rowspan over the call's callcenter rows. -->
                    <td
                      data-testid="document-cell"
                      class="px-3 py-2 font-mono-feat text-muted-foreground"
                      [class.border-l]="docSpan > 1"
                      [class.border-border]="docSpan > 1"
                      [attr.rowspan]="docSpan > 1 ? docSpan : null"
                    >
                      <div class="flex items-center justify-between gap-2">
                        <span>{{ row.documentId ?? '—' }}</span>
                        @if (isDeletable(row)) {
                          <button
                            hlmBtn
                            variant="ghost"
                            size="icon"
                            class="h-9 w-9 text-muted-foreground/70 hover:bg-destructive hover:text-destructive-foreground"
                            [hlmTooltip]="isCascadeDeletable(row) ? 'Delete all callcenter charges of this call' : 'Delete this row'"
                            [attr.aria-label]="isCascadeDeletable(row) ? 'Delete all callcenter charges of this call' : 'Delete this row'"
                            data-testid="billing-delete-row"
                            (click)="deleteRow(row); $event.stopPropagation()"
                          >
                            <hlm-icon name="lucideTrash2" size="1.1rem" />
                          </button>
                        }
                      </div>
                    </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div
            class="flex flex-wrap items-center justify-between gap-4 border-t border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
          >
            <span class="font-mono-feat">
              @if (pageRange().total === 0) {
                0 rows
              } @else {
                Showing {{ pageRange().from }}–{{ pageRange().to }} of {{ pageRange().total }}
              }
            </span>

            <div class="flex items-center gap-3">
              <span class="flex items-center gap-2">
                <span class="inline-block h-3 w-3 rounded-sm bg-destructive/20"></span>
                <span>Already invoiced (read-only)</span>
              </span>

              @if (totalPages() > 1) {
                <div class="flex items-center gap-1">
                  <button
                    hlmBtn variant="ghost" size="icon"
                    class="h-9 w-9 text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                    [disabled]="currentPage() === 0"
                    hlmTooltip="Previous page"
                    (click)="prevPage()"
                  >
                    <hlm-icon name="lucideChevronLeft" size="1.1rem" />
                  </button>
                  <span class="font-mono-feat text-foreground">
                    {{ currentPage() + 1 }} / {{ totalPages() }}
                  </span>
                  <button
                    hlmBtn variant="ghost" size="icon"
                    class="h-9 w-9 text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                    [disabled]="currentPage() >= totalPages() - 1"
                    hlmTooltip="Next page"
                    (click)="nextPage()"
                  >
                    <hlm-icon name="lucideChevronRight" size="1.1rem" />
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- BOTTOM ACTIONS -->
      <div class="flex justify-end">
        <button
          hlmBtn
          variant="default"
          (click)="openAddDialog()"
          [disabled]="addBillingDisabled()"
          [attr.title]="addBillingDisabled()
            ? 'Callcenter Calls are generated automatically and cannot be added manually.'
            : null"
        >
          <hlm-icon name="lucidePlus" size="0.9rem" />
          Add Billing
        </button>
      </div>
    </div>
  }
</section>
